/**
 * The thread-list server concern: the list query (cache shared with the ai-teammate drawer) plus the
 * rename/pin/delete mutations and their per-row busy state.
 *
 * Delete RECONCILIATION deliberately stays in the controller — it is navigation business, not list business.
 */

import { useQueryClient } from "@tanstack/react-query"
import { useCallback, useMemo, useState } from "react"
import { useDeleteThread } from "connected-components/ai-teammate/hooks/delete-thread"
import { useUpdateThread } from "connected-components/ai-teammate/hooks/update-thread"
import type { FetchThreadsResponse, Thread } from "connected-components/ai-teammate/types/thread"
import { filterThreadsByAgentType } from "connected-components/ai-teammate/utils/filter-threads"
import { getThreadsQueryKey } from "connected-components/ai-teammate/utils/threads-cache"
import { useGetQuery } from "hooks/queries"
import { threadsSupportPinning } from "./thread-partition"
import { usePinThread } from "./use-pin-thread"

const EMPTY_THREADS: Thread[] = []

export interface ThreadListController {
  threads: Thread[]
  /** Shared with the controller, whose optimistic row writes must hit the same cache entry. */
  threadsKey: ReturnType<typeof getThreadsQueryKey>
  /** True once the list reached a terminal state — success OR error (never hang a gate). */
  isThreadsLoaded: boolean
  isThreadsErrored: boolean
  /** The failure itself, so a caller can report the same taxonomy bucket the other operations report. */
  threadsError: unknown
  /** True once the thread data carries the `pinned` field — the gate that reveals the pin UI. */
  canPinThreads: boolean
  /** The thread with a rename/pin in flight, so a row can scope its own busy state. */
  mutatingThreadId: string | null
  renameThread: (threadId: string, title: string) => Promise<void>
  /** Toggle a thread's pin: optimistic flip, rollback + notify on failure, persisted via PATCH.
   *  Returns the state it is moving TO, so a caller reporting the toggle cannot disagree with the mutation. */
  pinThread: (threadId: string) => boolean
  /** The raw DELETE; the caller owns the reconciliation that follows. */
  deleteThreadMutation: ReturnType<typeof useDeleteThread>["deleteThread"]
}

/** `isActive` false keeps the list idle: the app-level provider must not fetch for an account that never opened Kelly (ADR 0018). */
export function useThreadList(accountAlias: string, isActive = true): ThreadListController {
  const queryClient = useQueryClient()

  const threadsKey = useMemo(() => getThreadsQueryKey(accountAlias), [accountAlias])
  const { data: threadsData, isSuccess: isThreadsSuccess, isError: isThreadsErrored, error: threadsError } = useGetQuery<FetchThreadsResponse>({
    key: threadsKey,
    url: `/api/${accountAlias}/ai/chat/threads`,
    enabled: isActive,
  })
  const threads = useMemo(
    () => filterThreadsByAgentType(threadsData?.threads ?? EMPTY_THREADS, true),
    [threadsData],
  )
  const isThreadsLoaded = isThreadsSuccess || isThreadsErrored
  const canPinThreads = useMemo(() => threadsSupportPinning(threads), [threads])

  const { updateThread } = useUpdateThread()
  const { deleteThread: deleteThreadMutation } = useDeleteThread()
  const { pinThread: pinThreadMutation } = usePinThread()
  // Which row is mutating — a rename/pin disables only its own menu, not the whole list.
  const [mutatingThreadId, setMutatingThreadId] = useState<string | null>(null)

  const renameThread = useCallback(
    async (threadId: string, title: string) => {
      setMutatingThreadId(threadId)
      try {
        await updateThread({ accountAlias, threadId, title })
      } catch {
        // The mutation hook already notified; swallow so callers' handlers don't crash.
      } finally {
        setMutatingThreadId((current) => (current === threadId ? null : current))
      }
    },
    [updateThread, accountAlias],
  )

  // Target state comes from the cache, not the render closure — a rapid re-click must not toggle off a stale value.
  const pinThread = useCallback(
    (threadId: string) => {
      const cached = queryClient.getQueryData<FetchThreadsResponse>(threadsKey)
      const isPinned = cached?.threads?.find((thread) => thread.threadId === threadId)?.pinned === true
      setMutatingThreadId(threadId)
      void (async () => {
        try {
          await pinThreadMutation({ accountAlias, threadId, pinned: !isPinned })
        } catch {
          // The mutation hook already rolled back the optimistic write and notified.
        } finally {
          setMutatingThreadId((current) => (current === threadId ? null : current))
        }
      })()
      return !isPinned
    },
    [pinThreadMutation, accountAlias, queryClient, threadsKey],
  )

  return {
    threads,
    threadsKey,
    isThreadsLoaded,
    isThreadsErrored,
    threadsError,
    canPinThreads,
    mutatingThreadId,
    renameThread,
    pinThread,
    deleteThreadMutation,
  }
}
